import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import AuthPopup from '../../components/AuthPopup/AuthPopup';
import CartButton from '../../components/CartButton';
import CartPopup from '../../components/CartPopup';
import Map from '../../components/Map/Map';
import PaymentPopup from '../../components/PaymentPopup/PaymentPopup';
import PaymentSuccessPopup from '../../components/PaymentSuccessPopup/PaymentSuccessPopup';
import { Toast } from '../../components/Toast';
import authService from '../../services/auth.service';
import checkoutStateService from '../../services/checkout-state.service';
import dataService from '../../services/data.service';
import mapCacheService from '../../services/mapCache.service';
import socketService, {
  DataResponse,
  MapUpdateData,
} from '../../services/socket.service';
import { MapData } from '../../ultis/types';
import './home.css';

const MAP_ID = 'main-map';

const Home: React.FC = () => {
  const [mapdata, setMapdata] = useState<MapData[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [showCartPopup, setShowCartPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showPaymentSuccessPopup, setShowPaymentSuccessPopup] = useState(false);
  const [paymentBillId, setPaymentBillId] = useState<string | null>(null);
  const [paymentExpAt, setPaymentExpAt] = useState<number>(0);
  const [paymentQr, setPaymentQr] = useState<string | null>(null);
  const [paymentSuccessBillId, setPaymentSuccessBillId] = useState<
    string | null
  >(null);
  const [paymentSuccessMessage, setPaymentSuccessMessage] =
    useState<string>('');
  const [mapResetKey, setMapResetKey] = useState(0);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false,
  });

  // Check authentication status
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = authService.getAccessToken();
      if (token) {
        try {
          const response = await authService.validateToken();
          if (response.success && response.username) {
            setIsLoggedIn(true);
            setUserId(response.username);
            console.log(
              '[HOME] Initial auth check: logged in as',
              response.username
            );
          } else {
            setIsLoggedIn(false);
            setUserId(null);
            console.log('[HOME] Initial auth check: not logged in');
          }
        } catch (error) {
          console.error('Token validation error:', error);
          setIsLoggedIn(false);
          setUserId(null);
        }
      } else {
        setIsLoggedIn(false);
        setUserId(null);
        console.log('[HOME] Initial auth check: no token found');
      }
    };

    checkAuthStatus();

    // Listen for auth state changes from other components (e.g., Header)
    const handleAuthStateChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log(
        '[HOME] Received auth-state-changed event:',
        customEvent.detail
      );

      if (customEvent.detail.isLoggedIn) {
        setIsLoggedIn(true);
        setUserId(customEvent.detail.username);
        console.log(
          '[HOME] Auth state updated from event: logged in as',
          customEvent.detail.username
        );
      } else {
        setIsLoggedIn(false);
        setUserId(null);
        console.log('[HOME] Auth state updated from event: logged out');
      }
    };

    window.addEventListener('auth-state-changed', handleAuthStateChanged);

    return () => {
      window.removeEventListener('auth-state-changed', handleAuthStateChanged);
    };
  }, []);

  // Merge updated cells with existing data
  const mergeMapData = useCallback((existingData: any[], updatedCells: any[]): any[] => {
    // Create a lookup object by cell index
    const dataLookup: { [key: number]: any } = {};

    // Add all existing cells
    existingData.forEach((cell) => {
      dataLookup[cell.i] = cell;
    });

    // Update or add new cells
    updatedCells.forEach((cell) => {
      dataLookup[cell.i] = cell;
    });

    // Convert back to array
    return Object.values(dataLookup);
  }, []);

  // Setup socket listeners - moved outside useEffect to be reusable
  const setupSocketListeners = useCallback(() => {
    // Remove old listeners to prevent duplicates when reconnecting
    socketService.offJoinSuccess();
    socketService.offDataResponse();
    socketService.offCellUpdate();
    socketService.offMapUpdate();
    socketService.offError();

      // Listen for join success - then request data
      socketService.onJoinSuccess(async (data) => {
        console.log('Join success:', data);

        // Check IndexedDB for cache
        const cachedData = await mapCacheService.getMapCache();

        if (cachedData && cachedData.timestamp) {
          console.log(
            '[CACHE] Found cached data, requesting sync from timestamp:',
            cachedData.timestamp
          );
          // Load cached data first for instant display
          dataService.setMapData(cachedData.data);
          setMapdata(dataService.mapData);

          // Then request sync
          socketService.syncData(MAP_ID, cachedData.timestamp);
        } else {
          console.log('[CACHE] No cache found, requesting full data');
          socketService.getData(MAP_ID);
        }
      });

      // Listen for data response (both get-data and sync-data)
      socketService.onDataResponse(async (response: DataResponse) => {
        console.log('Received data response:', response);
        const { data, timestamp } = response;

        if (data && data.length > 0) {
          // Check if this is sync data or full data
          const cachedData = await mapCacheService.getMapCache();

          let newMapData;
          if (cachedData && cachedData.timestamp) {
            // This is sync data - merge with existing data
            console.log(`[SYNC] Received ${data.length} updated cells`);
            const mergedData = mergeMapData(cachedData.data, data);
            console.log('onDataResponse mergedData', mergedData);
            await mapCacheService.saveMapCache(mergedData, timestamp);
            newMapData = dataService.setMapData(mergedData);
          } else {
            // This is full data
            console.log(`[FULL] Received ${data.length} cells`);
            await mapCacheService.saveMapCache(data, timestamp);
            newMapData = dataService.setMapData(data);
          }

          console.log('dataService.mapData', dataService.mapData);
          // Create new array reference to trigger re-render
          setMapdata([...newMapData]);
        } else {
          // Data is empty (database is empty) - still need to initialize map structure
          const cachedData = await mapCacheService.getMapCache();

          if (cachedData && cachedData.timestamp) {
            // We have cached data, no updates needed
            console.log('[SYNC] No updates, data is up to date');
          } else {
            // First time loading with empty database - initialize map structure with empty areas
            console.log(
              '[FULL] Database is empty, initializing map structure with empty areas'
            );
            const emptyData: any[] = [];
            await mapCacheService.saveMapCache(emptyData, timestamp);
            const newMapData = dataService.setMapData(emptyData);
            console.log('dataService.mapData (empty)', dataService.mapData);
            // Create new array reference to trigger re-render
            setMapdata([...newMapData]);
          }
        }
      });

      // Listen for cell updates (real-time from other clients)
      socketService.onCellUpdate(async (response: DataResponse) => {
        const cachedData = await mapCacheService.getMapCache();
        if (cachedData) {
          // response.data is a single cell or array of cells
          const updatedCells = Array.isArray(response.data)
            ? response.data
            : [response.data];
          const mergedData = mergeMapData(cachedData.data, updatedCells);
          await mapCacheService.saveMapCache(mergedData, response.timestamp);

          // Use return value and create new array reference to trigger re-render
          const newMapData = dataService.setMapData(mergedData);
          setMapdata([...newMapData]);
        }
      });

      // Listen for map updates (real-time from other clients)
      socketService.onMapUpdate(async (updateData: MapUpdateData) => {
        console.log('Received real-time map update:', updateData);

        const cachedData = await mapCacheService.getMapCache();
        if (cachedData) {
          console.log('🔴🔴🔴 updateData', updateData);
          const mergedData = mergeMapData(cachedData.data, updateData.data);
          console.log('🔴🔴🔴 mergedData', mergedData);
          await mapCacheService.saveMapCache(mergedData, updateData.timestamp);
          const newMapData = dataService.setMapData(mergedData);
          console.log('🔴🔴🔴 newMapData', newMapData);
          setMapdata([...newMapData]);
        }
      });

    // Listen for general errors
    socketService.onError((data) => {
      console.error('Socket error:', data);
    });
  }, [mergeMapData]);

  useEffect(() => {
    // Get user ID from auth service or socket
    const getUserId = () => {
      // Try to get user ID from socket first, then from auth service
      const socket = socketService.getSocket();
      if (socket && socket.userId) {
        return socket.userId;
      }
      // Use authenticated user ID if available
      if (isLoggedIn) {
        return userId;
      }
      // For now, return null if not authenticated
      return null;
    };

    setUserId(getUserId());

    // Initialize IndexedDB cache service
    mapCacheService.init().catch((error) => {
      console.error('[HOME] Error initializing map cache service:', error);
    });

    // Connect to socket
    const connectSocket = async () => {
      try {
        await socketService.connect();

        // Set up event listeners first
        setupSocketListeners();

        // Listen for reconnection events (when socket reconnects after login)
        const socket = socketService.getSocket();
        if (socket) {
          socket.on('connect', () => {
            console.log(
              '[RECONNECT] Socket reconnected, setting up listeners again...'
            );
            // Setup listeners again after reconnect
            setupSocketListeners();
          });
        }
      } catch (error) {
        console.error('Failed to connect to socket:', error);
      }
    };

    connectSocket();

    // Cleanup on unmount
    return () => {
      socketService.disconnect();
    };
  }, [setupSocketListeners, isLoggedIn, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user needs to login and show popup
  // IMPORTANT: Use useCallback with [isLoggedIn, userId] dependencies to avoid stale closure
  const checkAuthAndShowPopup = useCallback(() => {
    console.log(
      '[HOME] checkAuthAndShowPopup - isLoggedIn:',
      isLoggedIn,
      'userId:',
      userId
    );
    if (!isLoggedIn) {
      console.log('[HOME] User not logged in, showing auth popup');
      setShowAuthPopup(true);
      return false;
    }
    console.log('[HOME] User is logged in, allowing action');
    return true;
  }, [isLoggedIn, userId]);

  const handleLoginSuccess = async (token: string, user: string) => {
    console.log('[HOME] handleLoginSuccess CALLED!!!', { token, user });
    console.log('[HOME] Current isLoggedIn:', isLoggedIn);
    console.log('[HOME] Current userId:', userId);

    setIsLoggedIn(true);
    setUserId(user);
    setShowAuthPopup(false);

    console.log('[HOME] States updated: isLoggedIn=true, userId=', user);

    // Reconnect socket with new token (whether connected or not)
    try {
      console.log('[HOME] Reconnecting socket after login...');
      await socketService.reconnect();
      console.log('[HOME] Socket reconnected successfully with authentication');
      
      // IMPORTANT: Setup listeners again after reconnect
      // This ensures we can receive map updates after login
      console.log('[HOME] Setting up socket listeners again after reconnect...');
      setupSocketListeners();
    } catch (error) {
      console.error('[HOME] Failed to reconnect socket after login:', error);
    }

    // Dispatch event to notify other components (like Header) about login
    window.dispatchEvent(
      new CustomEvent('auth-state-changed', {
        detail: { isLoggedIn: true, username: user, token },
      })
    );
    console.log('[HOME] Dispatched auth-state-changed event for Header');

    showToast('Đăng nhập thành công!', 'success');
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  // Listen for toast events from Map component
  useEffect(() => {
    const handleShowToast = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.message && customEvent.detail?.type) {
        showToast(customEvent.detail.message, customEvent.detail.type);
      }
    };

    window.addEventListener('show-toast', handleShowToast);

    return () => {
      window.removeEventListener('show-toast', handleShowToast);
    };
  }, []);

  // Setup checkout list listeners
  useEffect(() => {
    // Listen for checkout list (when joining)
    const handleCheckoutList = (response: any) => {
      console.log('[Home] Received checkout list:', response);
      if (response.cells && Array.isArray(response.cells)) {
        checkoutStateService.updateCheckoutList(response.cells);
      }
    };

    // Listen for checkout updates (broadcast)
    const handleCheckoutUpdate = (response: any) => {
      console.log('[Home] Received checkout update:', response);
      if (response.cells && Array.isArray(response.cells)) {
        checkoutStateService.addCheckoutCells(response.cells);
      }
    };

    // Listen for payment success
    const handlePaymentSuccess = (response: any) => {
      console.log('[Home] Received payment success event:', response);
      console.log(
        '[Home] Response type:',
        typeof response,
        'Has billId:',
        !!response?.billId
      );

      if (response && response.billId) {
        console.log(
          '[Home] Processing payment success - closing payment popup and showing success popup'
        );
        // Close payment popup if open
        setShowPaymentPopup(false);
        setPaymentBillId(null);
        setPaymentExpAt(0);
        setPaymentQr(null);

        // Show success popup
        setPaymentSuccessBillId(response.billId);
        setPaymentSuccessMessage(
          response.message || 'Giao dịch thanh toán đã được xác nhận thành công'
        );
        setShowPaymentSuccessPopup(true);

        console.log(
          '[Home] Success popup should be visible now with billId:',
          response.billId
        );
      } else {
        console.warn(
          '[Home] Payment success event missing billId. Response:',
          response
        );
      }
    };

    socketService.onCheckoutList(handleCheckoutList);
    socketService.onCheckoutUpdate(handleCheckoutUpdate);
    socketService.onPaymentSuccess(handlePaymentSuccess);

    // Cleanup expired items periodically
    const interval = setInterval(() => {
      checkoutStateService.removeExpiredItems();
    }, 10000); // Check every 10 seconds

    return () => {
      socketService.offCheckoutList(handleCheckoutList);
      socketService.offCheckoutUpdate(handleCheckoutUpdate);
      socketService.offPaymentSuccess(handlePaymentSuccess);
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Trang chủ</title>

        <meta
          name="description"
          content="pixel.io.vn là nơi bạn có thể khám phá, sáng tạo và sỡ hữu các ô pixel."
        />

        {/* og */}
        <meta property="og:title" content="Trang chủ" />
        <meta
          property="og:description"
          content="pixel.io.vn là nơi bạn có thể khám phá, sáng tạo và sỡ hữu các ô pixel."
        />
        <meta property="og:image" content="/logo512.jpg" />
      </Helmet>

      <div className="home">
        {/* Map Component */}
        {mapdata && mapdata.length && (
          <Map
            key={`map-${isLoggedIn ? 'logged-in' : 'logged-out'}`} // Force remount khi login state thay đổi
            data={mapdata}
            uid={userId}
            isLoggedIn={isLoggedIn}
            onRequireAuth={checkAuthAndShowPopup}
            resetTrigger={mapResetKey}
          />
        )}

        {/* Auth Popup */}
        <AuthPopup
          isOpen={showAuthPopup}
          onClose={() => {
            console.log('[HOME] AuthPopup onClose called');
            setShowAuthPopup(false);
            // Reset Map interaction flag khi đóng popup
            setMapResetKey((prev) => prev + 1);
          }}
          onLoginSuccess={handleLoginSuccess}
          onShowToast={showToast}
        />

        {/* Cart Button */}
        <CartButton onClick={() => setShowCartPopup(true)} />

        {/* Cart Popup */}
        <CartPopup
          isOpen={showCartPopup}
          onClose={() => setShowCartPopup(false)}
          onCheckoutSuccess={(billId, expAt, qr) => {
            setPaymentBillId(billId);
            setPaymentExpAt(expAt);
            setPaymentQr(qr || null);
            setShowPaymentPopup(true);
          }}
        />

        {/* Payment Popup */}
        {paymentBillId && (
          <PaymentPopup
            isOpen={showPaymentPopup}
            billId={paymentBillId}
            expAt={paymentExpAt}
            qr={paymentQr || undefined}
            onClose={() => {
              setShowPaymentPopup(false);
              setPaymentBillId(null);
              setPaymentExpAt(0);
              setPaymentQr(null);
            }}
          />
        )}

        {/* Payment Success Popup */}
        {paymentSuccessBillId && (
          <PaymentSuccessPopup
            isOpen={showPaymentSuccessPopup}
            billId={paymentSuccessBillId}
            message={paymentSuccessMessage}
            onClose={() => {
              setShowPaymentSuccessPopup(false);
              setPaymentSuccessBillId(null);
              setPaymentSuccessMessage('');
            }}
          />
        )}

        {/* Toast Notification */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      </div>
    </>
  );
};

export default Home;
